// MeetingsList.js
import React, { useState } from 'react';
import { 
  Video, 
  Users, 
  Calendar, 
  Clock, 
  MapPin, 
  ChevronRight,
  Phone,
  Monitor,
  Coffee,
  Briefcase,
  AlertCircle,
  CheckCircle,
  XCircle,
  User,
  MoreVertical,
  Link,
  Bell
} from 'lucide-react';
import BackendTemplateConfig from '@/configs/BackendTemplateConfig';

const MeetingsList = () => {
  const config = BackendTemplateConfig;
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  
  // Meeting types configuration
  const meetingTypes = {
    video: { icon: Video, label: 'Video Call', color: config.colors.primary['600'] },
    phone: { icon: Phone, label: 'Phone Call', color: config.colors.success['600'] },
    inPerson: { icon: Users, label: 'In Person', color: config.colors.warning['600'] },
    presentation: { icon: Monitor, label: 'Presentation', color: config.colors.danger['600'] },
    casual: { icon: Coffee, label: 'Casual', color: 'bg-orange-600' },
    formal: { icon: Briefcase, label: 'Formal', color: config.colors.neutral['700'] }
  };

  // Meeting status colors
  const statusConfig = {
    upcoming: { icon: Clock, color: config.colors.primary.text, bg: config.colors.primary['50'], label: 'Upcoming' },
    inProgress: { icon: AlertCircle, color: config.colors.success.text, bg: config.colors.success['50'], label: 'In Progress' },
    completed: { icon: CheckCircle, color: config.colors.neutral.text.muted, bg: config.colors.neutral['100'], label: 'Completed' },
    cancelled: { icon: XCircle, color: config.colors.danger.text, bg: config.colors.danger['50'], label: 'Cancelled' }
  };

  // Sample meetings data
  const meetings = [
    {
      id: 1,
      title: "Q4 Strategy Planning",
      type: "video",
      status: "upcoming",
      startTime: "10:00 AM",
      endTime: "11:30 AM",
      duration: "1h 30m",
      timeUntil: "in 2 hours",
      date: "Today",
      location: "Zoom Meeting Room",
      meetingLink: "https://zoom.us/j/123456789",
      organizer: {
        name: "Marco Rossi",
        role: "CEO",
        avatar: null
      },
      participants: [
        { id: 1, name: "Giulia Bianchi", role: "CFO", status: "confirmed", avatar: null },
        { id: 2, name: "Alessandro Ferrari", role: "CTO", status: "confirmed", avatar: null },
        { id: 3, name: "Sofia Romano", role: "CMO", status: "tentative", avatar: null },
        { id: 4, name: "Luca Moretti", role: "COO", status: "confirmed", avatar: null }
      ],
      description: "Quarterly strategy review and planning for Q4 2024. Focus on budget allocation and key initiatives.",
      agenda: ["Budget Review", "Market Analysis", "Product Roadmap", "Team Expansion"],
      priority: "high"
    },
    {
      id: 2,
      title: "Team Standup",
      type: "casual",
      status: "inProgress",
      startTime: "9:00 AM",
      endTime: "9:15 AM",
      duration: "15m",
      timeUntil: "now",
      date: "Today",
      location: "Conference Room A",
      organizer: {
        name: "Alessandro Ferrari",
        role: "Engineering Lead",
        avatar: null
      },
      participants: [
        { id: 1, name: "Pietro Conti", role: "Frontend Dev", status: "confirmed", avatar: null },
        { id: 2, name: "Maria Greco", role: "Backend Dev", status: "confirmed", avatar: null },
        { id: 3, name: "Antonio Bruno", role: "DevOps", status: "confirmed", avatar: null }
      ],
      description: "Daily engineering team sync to discuss progress and blockers.",
      agenda: ["Yesterday's Progress", "Today's Goals", "Blockers"],
      priority: "medium"
    },
    {
      id: 3,
      title: "Client Presentation",
      type: "presentation",
      status: "upcoming",
      startTime: "2:00 PM",
      endTime: "3:30 PM",
      duration: "1h 30m",
      timeUntil: "in 5 hours",
      date: "Today",
      location: "Board Room",
      organizer: {
        name: "Sofia Romano",
        role: "Sales Director",
        avatar: null
      },
      participants: [
        { id: 1, name: "External Client", role: "Acme Corp", status: "confirmed", avatar: null },
        { id: 2, name: "Marco Rossi", role: "CEO", status: "confirmed", avatar: null },
        { id: 3, name: "Giulia Bianchi", role: "CFO", status: "tentative", avatar: null }
      ],
      description: "Final presentation for Q4 partnership proposal with Acme Corp.",
      agenda: ["Company Overview", "Proposal Details", "Pricing", "Q&A"],
      priority: "high"
    },
    {
      id: 4,
      title: "1:1 Review",
      type: "phone",
      status: "upcoming",
      startTime: "4:00 PM",
      endTime: "4:30 PM",
      duration: "30m",
      timeUntil: "in 7 hours",
      date: "Today",
      location: "Phone Call",
      organizer: {
        name: "Giulia Bianchi",
        role: "Manager",
        avatar: null
      },
      participants: [
        { id: 1, name: "Luca Moretti", role: "Senior Developer", status: "confirmed", avatar: null }
      ],
      description: "Monthly performance review and career development discussion.",
      agenda: ["Performance Review", "Goal Setting", "Career Path"],
      priority: "medium"
    },
    {
      id: 5,
      title: "Product Demo",
      type: "video",
      status: "completed",
      startTime: "3:00 PM",
      endTime: "4:00 PM",
      duration: "1h",
      timeUntil: "yesterday",
      date: "Yesterday",
      location: "Teams Meeting",
      meetingLink: "https://teams.microsoft.com/meet/123456",
      organizer: {
        name: "Pietro Conti",
        role: "Product Manager",
        avatar: null
      },
      participants: [
        { id: 1, name: "Marketing Team", role: "5 members", status: "confirmed", avatar: null },
        { id: 2, name: "Sales Team", role: "3 members", status: "confirmed", avatar: null }
      ],
      description: "New feature demonstration for internal teams.",
      agenda: ["Feature Overview", "Use Cases", "Rollout Plan"],
      priority: "low"
    },
    {
      id: 6,
      title: "Budget Planning",
      type: "formal",
      status: "cancelled",
      startTime: "11:00 AM",
      endTime: "12:00 PM",
      duration: "1h",
      timeUntil: "cancelled",
      date: "Tomorrow",
      location: "Conference Room B",
      organizer: {
        name: "Giulia Bianchi",
        role: "CFO",
        avatar: null
      },
      participants: [
        { id: 1, name: "Finance Team", role: "4 members", status: "declined", avatar: null }
      ],
      description: "Rescheduled to next week due to conflicts.",
      agenda: ["Q1 Budget", "Resource Allocation"],
      priority: "high"
    }
  ];

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return config.colors.danger.text;
      case 'medium': return config.colors.warning.text;
      case 'low': return config.colors.success.text;
      default: return config.colors.neutral.text.muted;
    }
  };

  const getParticipantStatusIcon = (status) => {
    switch(status) {
      case 'confirmed': return { icon: CheckCircle, color: config.colors.success.text };
      case 'tentative': return { icon: AlertCircle, color: config.colors.warning.text };
      case 'declined': return { icon: XCircle, color: config.colors.danger.text };
      default: return { icon: Clock, color: config.colors.neutral.text.muted };
    }
  };

  const handleMeetingClick = (meeting) => {
    setSelectedMeeting(selectedMeeting?.id === meeting.id ? null : meeting);
  };

  return (
    <div className={`${config.container.minHeight} ${config.container.backgroundColor} ${config.container.paddingLarge}`}>
      <div className={`${config.container.maxWidth} ${config.container.marginX}`}>
        {/* Header */}
        <div className={`${config.layout.flexBetween} mb-6`}>
          <h1 className={`${config.typography.pageTitle} ${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.medium}`}>
            <Calendar className={`${config.icons.large} ${config.colors.primary.text}`} />
            Meetings Schedule
          </h1>
          <div className={`${config.layout.flex} ${config.layout.flexGap.medium}`}>
            <button className={config.buttons.secondary}>
              <Bell className={`${config.icons.medium} mr-2`} />
              Reminders
            </button>
            <button className={config.buttons.primary}>
              <Video className={`${config.icons.medium} mr-2`} />
              New Meeting
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className={`${config.layout.flex} ${config.layout.flexGap.medium} mb-6 border-b ${config.colors.neutral.border.default}`}>
          {Object.entries(statusConfig).map(([key, status]) => {
            const count = meetings.filter(m => m.status === key).length;
            return (
              <button
                key={key}
                className={`pb-3 px-1 border-b-2 ${config.animations.transition.colors} ${
                  key === 'upcoming' 
                    ? `${config.colors.primary.border} ${config.colors.primary.text}` 
                    : 'border-transparent ' + config.colors.neutral.text.muted
                } hover:${config.colors.neutral.text.primary}`}
              >
                <span className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
                  <status.icon className={config.icons.medium} />
                  {status.label}
                  {count > 0 && (
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${status.bg} ${status.color}`}>
                      {count}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {/* Meetings List */}
        <div className={config.card.spacing}>
          {meetings.map((meeting) => {
            const meetingType = meetingTypes[meeting.type];
            const StatusIcon = statusConfig[meeting.status].icon;
            const isExpanded = selectedMeeting?.id === meeting.id;

            return (
              <div 
                key={meeting.id}
                className={`${config.card.hover} ${config.animations.transition.all} ${
                  isExpanded ? `${config.shadows.lg} scale-[1.01]` : ''
                }`}
                onClick={() => handleMeetingClick(meeting)}
              >
                <div className={config.card.padding}>
                  {/* Main Meeting Row */}
                  <div className={`${config.layout.flex} ${config.layout.flexGap.large}`}>
                    {/* Meeting Type Icon */}
                    <div className={`p-3 ${config.borderRadius.lg} ${meetingType.color} bg-opacity-10 flex-shrink-0`}>
                      <meetingType.icon className={`${config.icons.large} ${meetingType.color.replace('bg-', 'text-')}`} />
                    </div>

                    {/* Meeting Details - Left Aligned */}
                    <div className="flex-1 min-w-0">
                      <div className={`${config.layout.flex} ${config.layout.flexGap.medium} mb-1`}>
                        <h3 className={`${config.typography.heading} text-base font-semibold`}>
                          {meeting.title}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs ${config.borderRadius.md} ${statusConfig[meeting.status].bg} ${statusConfig[meeting.status].color}`}>
                          {statusConfig[meeting.status].label}
                        </span>
                        <span className={`text-xs font-medium ${getPriorityColor(meeting.priority)}`}>
                          {meeting.priority.toUpperCase()}
                        </span>
                      </div>
                      
                      <p className={`${config.typography.body} mb-2`}>
                        {meeting.description}
                      </p>

                      {/* Meeting Info Row */}
                      <div className={`${config.layout.flex} flex-wrap ${config.layout.flexGap.large} ${config.typography.small}`}>
                        <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
                          <Clock className={config.icons.small} />
                          <span>{meeting.startTime} - {meeting.endTime} ({meeting.duration})</span>
                        </div>
                        <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
                          <MapPin className={config.icons.small} />
                          <span>{meeting.location}</span>
                        </div>
                        <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
                          <User className={config.icons.small} />
                          <span>Organizer: {meeting.organizer.name}</span>
                        </div>
                        <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
                          <Users className={config.icons.small} />
                          <span>{meeting.participants.length} participants</span>
                        </div>
                      </div>
                    </div>

                    {/* Right Side Actions */}
                    <div className="flex flex-col items-end justify-between flex-shrink-0">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Options for:', meeting.title);
                        }}
                        className={config.buttons.ghost}
                      >
                        <MoreVertical className={config.icons.normal} />
                      </button>
                      
                      <div className={`${config.layout.flexCol} items-end ${config.layout.flexGap.small}`}>
                        <span className={`px-3 py-1 ${config.borderRadius.full} text-sm font-medium ${
                          meeting.status === 'inProgress' 
                            ? `${config.colors.success['50']} ${config.colors.success.text}` 
                            : meeting.status === 'upcoming'
                            ? `${config.colors.primary['50']} ${config.colors.primary.text}`
                            : `${config.colors.neutral['100']} ${config.colors.neutral.text.muted}`
                        }`}>
                          {meeting.status === 'inProgress' ? '🔴 In Progress' : meeting.timeUntil}
                        </span>
                        {meeting.meetingLink && meeting.status !== 'cancelled' && meeting.status !== 'completed' && (
                          <a 
                            href={meeting.meetingLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small} px-3 py-1 ${config.borderRadius.full} ${config.colors.primary['50']} ${config.colors.primary.text} ${config.animations.transition.colors} hover:${config.colors.primary['100']}`}
                          >
                            <Link className={config.icons.small} />
                            Join Meeting
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className={`mt-4 pt-4 border-t ${config.colors.neutral.border.light}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Participants */}
                        <div>
                          <h4 className={`${config.typography.label} mb-3`}>Participants</h4>
                          <div className={config.card.spacing}>
                            {meeting.participants.map(participant => {
                              const statusIcon = getParticipantStatusIcon(participant.status);
                              return (
                                <div key={participant.id} className={`${config.layout.flex} ${config.layout.flexBetween} p-2 ${config.borderRadius.md} ${config.colors.neutral['50']}`}>
                                  <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.medium}`}>
                                    <div className={`w-8 h-8 ${config.borderRadius.full} ${config.colors.neutral['200']} ${config.layout.flexCenter} flex`}>
                                      <User className={`${config.icons.small} ${config.colors.neutral.text.muted}`} />
                                    </div>
                                    <div>
                                      <p className={`text-sm font-medium ${config.colors.neutral.text.primary}`}>
                                        {participant.name}
                                      </p>
                                      <p className={config.typography.small}>
                                        {participant.role}
                                      </p>
                                    </div>
                                  </div>
                                  <statusIcon.icon className={`${config.icons.small} ${statusIcon.color}`} />
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Agenda */}
                        <div>
                          <h4 className={`${config.typography.label} mb-3`}>Agenda</h4>
                          <div className={config.card.spacing}>
                            {meeting.agenda.map((item, index) => (
                              <div key={index} className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.medium} p-2`}>
                                <span className={`w-6 h-6 ${config.borderRadius.full} ${config.colors.primary['100']} ${config.colors.primary.text} ${config.layout.flexCenter} flex text-xs font-medium`}>
                                  {index + 1}
                                </span>
                                <span className={config.typography.body}>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MeetingsList;